/**
 * The reference a user can quote when something fails on our side.
 *
 * Every API response carries the request's id in `X-Request-Id` (and 5xx JSON
 * bodies repeat it as `requestId`). The server records each error against that
 * id, so pasting it into System Admin → Errors finds the exact failure — and
 * any PDF/embedding/email worker it set off. Without it a report is "it broke
 * at about 3".
 *
 * Only 5xx: those are what the server records. A 4xx is the request being
 * refused (validation, permissions) and its message already says why.
 *
 * The full id, not a short form: the lookup matches the whole id, and an id is
 * copied or screenshotted far more often than read aloud.
 */

/** The server-side reference for a failed API call, or null. */
export function errorReference(err: unknown): string | null {
  const response = (err as any)?.response;
  if (!response || typeof response.status !== 'number' || response.status < 500) return null;
  const fromHeader = response.headers?.['x-request-id'];
  const fromBody = response.data && typeof response.data === 'object' ? response.data.requestId : undefined;
  const id = typeof fromHeader === 'string' && fromHeader ? fromHeader : fromBody;
  return typeof id === 'string' && /^[A-Za-z0-9-]{1,100}$/.test(id) ? id : null;
}

/** "Failed to save. (Ref: 3f2a…)" — or the message unchanged when there is no reference. */
export function withErrorReference(message: string, err: unknown): string {
  const ref = errorReference(err);
  return ref && !message.includes(ref) ? `${message} (Ref: ${ref})` : message;
}

/**
 * Response-interceptor step: put the reference into the error text the app
 * already shows. Most screens display `err.response.data.error` (or
 * `err.message`), so appending it there reaches them all without touching each
 * one. Mutates in place; never throws.
 */
export function attachErrorReference(error: any): void {
  try {
    const ref = errorReference(error);
    if (!ref) return;
    error.requestId = ref;
    const data = error.response.data;
    if (data && typeof data === 'object' && !(typeof Blob !== 'undefined' && data instanceof Blob)
        && typeof data.error === 'string') {
      data.error = withErrorReference(data.error, error);
    }
    if (typeof error.message === 'string') error.message = withErrorReference(error.message, error);
  } catch {
    // Diagnostics must never change what the caller sees beyond the text.
  }
}
